import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from 'utils/api';

const baseURL = '/container';

// Fetch all containers
export const fetchContainers = createAsyncThunk('container/fetchContainers', async (_, thunkAPI) => {
  const { rejectWithValue } = thunkAPI;
  try {
    const token = localStorage.getItem('token');
    const res = await api.get(`${baseURL}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res.data.data;
  } catch (error) {
    return rejectWithValue(error.message);
  }
});

// Fetch container by project ID
export const fetchContainerByProjectId = createAsyncThunk(
  'container/fetchContainerByProjectId',
  async (projectId, thunkAPI) => {
    const { rejectWithValue } = thunkAPI;
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`${baseURL}/byProjectId/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Create container (spacing/orientation)
export const createContainer = createAsyncThunk(
  'container/createContainer',
  async ({ id, ...containerData }, thunkAPI) => {
    const { rejectWithValue } = thunkAPI;
    try {
      const token = localStorage.getItem('token');
      const res = await api.post(
        baseURL,
        {
          x: containerData.x || 10.0,
          y: containerData.y || 5.0,
          vertical: containerData.vertical || false,
          allow_rotation: containerData.allow_rotation || false,
          multi_plate: containerData.multi_plate || false,
          project_id: id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Update container
export const updateContainer = createAsyncThunk(
  'container/updateContainer',
  async ({ id, ...containerData }, thunkAPI) => {
    const { rejectWithValue } = thunkAPI;
    try {
      const token = localStorage.getItem('token');
      const res = await api.put(`${baseURL}/${id}`, containerData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Delete container
export const deleteContainer = createAsyncThunk(
  'container/deleteContainer',
  async (containerId, thunkAPI) => {
    const { rejectWithValue } = thunkAPI;
    try {
      const token = localStorage.getItem('token');
      const response = await api.delete(`${baseURL}/${containerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const containerSlice = createSlice({
  name: 'container',
  initialState: {
    container: null,
    loading: false,
    error: null,
    containers: []
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch containers
      .addCase(fetchContainers.fulfilled, (state, action) => {
        state.loading = false;
        state.containers = action.payload || [];
      })
      .addCase(fetchContainers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContainers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch by project ID
      .addCase(fetchContainerByProjectId.fulfilled, (state, action) => {
        state.loading = false;
        state.containers = action.payload || [];
        state.container = action.payload?.[0] || null;
      })
      .addCase(fetchContainerByProjectId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContainerByProjectId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create container
      .addCase(createContainer.fulfilled, (state, action) => {
        state.loading = false;
        state.containers = [action.payload];
        state.container = action.payload;
      })
      .addCase(createContainer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createContainer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update container
      .addCase(updateContainer.fulfilled, (state, action) => {
        state.loading = false;
        state.containers = [action.payload];
        state.container = action.payload;
      })
      .addCase(updateContainer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateContainer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete container
      .addCase(deleteContainer.fulfilled, (state, action) => {
        state.loading = false;
        state.containers = [];
        state.container = null;
      })
      .addCase(deleteContainer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteContainer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export default containerSlice.reducer;